import { useEffect, useState, useCallback } from 'react';
import { useSession } from '@/Recoil/Session/Session';
import { useRoles } from '@/Recoil/Topik/Roles';
import { useExams } from '@/Recoil/Topik/Exams';
import { navigateTo } from '@/Utils/routing';
import RoleSection from '../Components/RoleSection';
import './index.scss';

const ROLE_ORDER: Record<string, number> = {
    admin:    4,
    manager:  3,
    lecturer: 1,
    student:  1,
};

const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}시간 ${minutes}분`;
};

function TopikExamListPage() {
    const { session } = useSession();
    const { roles, syncUserRole } = useRoles();
    const { examList, total, loading, error, syncExams, allocateExam } = useExams();

    const [allocateModalOpen, setAllocateModalOpen] = useState(false);
    const [selectedExamId, setSelectedExamId] = useState<string | null>(null);
    const [allocateEmail, setAllocateEmail] = useState('');
    const [allocating, setAllocating] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const userRole = roles[session.email];
    const roleLevel = ROLE_ORDER[userRole?.role] ?? 0;
    const hasMore = examList.length < total;

    useEffect(() => {
        if (!session.email) {
            navigateTo('/login?redirectTo=/topik/exam');
            return;
        }
        syncUserRole({ email: session.email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.email]);

    useEffect(() => {
        if (roleLevel >= ROLE_ORDER['manager']) {
            syncExams();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleLevel]);

    const openAllocateModal = useCallback((examid: string) => {
        setSelectedExamId(examid);
        setAllocateEmail('');
        setAllocateModalOpen(true);
    }, []);

    const closeAllocateModal = useCallback(() => {
        setAllocateModalOpen(false);
        setSelectedExamId(null);
        setAllocateEmail('');
    }, []);

    const handleAllocate = useCallback(async () => {
        if (!selectedExamId || !allocateEmail.trim()) { return; }

        setAllocating(true);
        try {
            const result = await allocateExam({
                examid: selectedExamId,
                userid: allocateEmail.trim()
            });

            if (result) {
                alert(`시험이 할당되었습니다.\nQuestionnaire ID: ${result.questionnaireid}`);
                closeAllocateModal();
            } else {
                alert('시험 할당에 실패했습니다.');
            }
        } finally {
            setAllocating(false);
        }
    }, [selectedExamId, allocateEmail, allocateExam, closeAllocateModal]);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !hasMore) { return; }

        setLoadingMore(true);
        try {
            await syncExams({ offset: examList.length });
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, syncExams, examList.length]);

    if (roleLevel < ROLE_ORDER['manager']) {
        return (
            <div className="TopikExamListPage">
                <div className="Container">
                    <header className="Header">MATE 한국어능력시험 시험 관리</header>
                    <RoleSection />
                    <div className="NoPermission">권한이 없습니다.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="TopikExamListPage">
            <div className="Container">
                <header className="Header">MATE 한국어능력시험 시험 관리</header>

                <RoleSection />

                {error && <div className="Error">{error}</div>}

                {loading && examList.length === 0 && (
                    <div className="Loading">로딩 중...</div>
                )}

                <table className="Table">
                    <thead>
                        <tr>
                            <th>{"시험 ID"}</th>
                            <th>{"제목"}</th>
                            <th>{"레벨"}</th>
                            <th>{"시간"}</th>
                            <th>{"총점"}</th>
                            <th className="right">{"액션"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {examList.length === 0 && !loading && (
                            <tr><td colSpan={6} className="Empty">시험이 없습니다.</td></tr>
                        )}
                        {examList.map((exam) => (
                            <tr key={exam.examid} onClick={() => navigateTo(`/topik/exam/${exam.examid}`)}>
                                <td className="ExamId">{exam.examid}</td>
                                <td className="Title">{exam.title}</td>
                                <td className="Level">
                                    <span className="LevelBadge">{exam.topik_level}</span>
                                    <span className="TargetBadge">{`Level ${exam.target_level}`}</span>
                                </td>
                                <td className="Duration">{formatDuration(exam.total_time)}</td>
                                <td className="Points">{`${exam.total_points}점`}</td>
                                <td className="ActionCell">
                                    <button
                                        className="AllocateButton"
                                        onClick={(e) => { e.stopPropagation(); openAllocateModal(exam.examid); }}
                                    >
                                        {"할당"}
                                    </button>
                                    <button
                                        className="DetailButton"
                                        onClick={(e) => { e.stopPropagation(); navigateTo(`/topik/exam/${exam.examid}`); }}
                                    >
                                        {"상세"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {total > 0 && (
                    <div className="Pagination">
                        <span>{`${examList.length} / ${total}개`}</span>
                        {hasMore && (
                            <button
                                className="LoadMoreButton"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? '로딩 중...' : '더 보기'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {allocateModalOpen && (
                <>
                    <div className="ModalOverlay active" onClick={closeAllocateModal}></div>
                    <div className="AllocateModal Modal active" onClick={(e) => e.stopPropagation()}>
                        <div className="ModalContent">
                            <div className="ModalHeader">
                                <h2 className="ModalTitle">시험 할당</h2>
                            </div>
                            <div className="ModalBody">
                                <p className="Description">시험을 할당할 사용자 정보를 입력하세요.</p>
                                <div className="InputGroup">
                                    <label className="InputLabel">사용자 ID 또는 이메일</label>
                                    <input
                                        type="text"
                                        className="Input"
                                        placeholder="addressid 또는 email"
                                        value={allocateEmail}
                                        onChange={(e) => setAllocateEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="ModalFooter">
                                <button className="ModalButton TertiaryButton" onClick={closeAllocateModal}>취소</button>
                                <button
                                    className="ModalButton PrimaryButton"
                                    onClick={handleAllocate}
                                    disabled={allocating || !allocateEmail.trim()}
                                >
                                    {allocating ? '할당 중...' : '할당하기'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default TopikExamListPage;